import { useEffect, useRef, type RefObject } from 'react';
import type { GridApi } from 'ag-grid-community';
import {
  useTabViewStateStore,
  type GridScrollAnchor,
} from '../store/tabViewStateStore';
import {
  captureAgGridScrollAnchor,
  getAgGridViewport,
  restoreAgGridScrollAnchor,
} from './agGridScrollAnchor';

const SCROLL_SAVE_THROTTLE_MS = 100;
const SCROLL_RESTORE_TIMEOUT_MS = 2000;
const SCROLL_RESTORE_MAX_WAIT_MS = 15000;
const SCROLL_RESTORE_INTERVAL_MS = 100;
const SCROLL_FRESH_TOP_PX = 1;
const SCROLL_RESTORE_SUCCESS_TOLERANCE_PX = 50;
const VIEWPORT_ATTACH_RETRY_MS = 50;
const VIEWPORT_ATTACH_MAX_ATTEMPTS = 40;

function isScrollNearAnchor({
  viewport,
  anchor,
  tolerancePx,
}: {
  viewport: HTMLElement;
  anchor: GridScrollAnchor;
  tolerancePx: number;
}): boolean {
  return Math.abs(viewport.scrollTop - anchor.scrollTop) <= tolerancePx;
}

function shouldSkipScrollRestore({
  viewport,
  anchor,
}: {
  viewport: HTMLElement;
  anchor: GridScrollAnchor;
}): boolean {
  const atFreshTop = viewport.scrollTop <= SCROLL_FRESH_TOP_PX;
  if (atFreshTop && anchor.scrollTop > SCROLL_FRESH_TOP_PX) {
    return false;
  }
  return isScrollNearAnchor({
    viewport,
    anchor,
    tolerancePx: SCROLL_RESTORE_SUCCESS_TOLERANCE_PX,
  });
}

function persistGridScrollAnchor({
  scrollId,
  anchor,
  force = false,
}: {
  scrollId: string;
  anchor: GridScrollAnchor;
  force?: boolean;
}): void {
  const { isScrollRestorePending, setGridScrollAnchor } =
    useTabViewStateStore.getState();
  if (!force && isScrollRestorePending(scrollId)) return;
  setGridScrollAnchor(scrollId, anchor);
}

export function useAgGridScrollPreservation<TRowData>({
  gridApi,
  containerRef,
  scrollId,
  isLoadingGrid,
}: {
  gridApi: GridApi<TRowData> | null;
  containerRef: RefObject<HTMLElement | null>;
  scrollId: string;
  isLoadingGrid: boolean;
}) {
  const lastAnchorRef = useRef<GridScrollAnchor | null>(null);
  const isLoadingGridRef = useRef(isLoadingGrid);
  isLoadingGridRef.current = isLoadingGrid;
  const restoreOutcomeRef = useRef<{
    attempted: boolean;
    succeeded: boolean;
  }>({ attempted: false, succeeded: false });

  useEffect(() => {
    if (!gridApi || gridApi.isDestroyed()) return;
    const activeGridApi = gridApi;
    restoreOutcomeRef.current = { attempted: false, succeeded: false };

    let cancelled = false;
    let throttleTimer: ReturnType<typeof setTimeout> | null = null;
    let attachRetryTimer: ReturnType<typeof setTimeout> | null = null;
    let attachedViewport: HTMLElement | null = null;
    let attachAttempts = 0;

    const captureLatestAnchor = () => {
      if (activeGridApi.isDestroyed()) return null;
      const anchor = captureAgGridScrollAnchor({
        gridApi: activeGridApi,
        containerRef,
      });
      if (!anchor) return null;
      lastAnchorRef.current = anchor;
      return anchor;
    };

    const handleScroll = () => {
      captureLatestAnchor();
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        throttleTimer = null;
        const anchor = lastAnchorRef.current;
        if (!anchor) return;
        persistGridScrollAnchor({ scrollId, anchor });
      }, SCROLL_SAVE_THROTTLE_MS);
    };

    const detachViewport = () => {
      if (!attachedViewport) return;
      attachedViewport.removeEventListener('scroll', handleScroll);
      attachedViewport = null;
    };

    const attachViewport = () => {
      if (cancelled || activeGridApi.isDestroyed()) return;

      const viewport = getAgGridViewport(containerRef);
      if (!viewport) {
        attachAttempts += 1;
        if (attachAttempts < VIEWPORT_ATTACH_MAX_ATTEMPTS) {
          attachRetryTimer = setTimeout(
            attachViewport,
            VIEWPORT_ATTACH_RETRY_MS,
          );
        }
        return;
      }

      attachedViewport = viewport;
      viewport.addEventListener('scroll', handleScroll, { passive: true });
    };

    attachViewport();

    return () => {
      cancelled = true;
      detachViewport();
      if (throttleTimer) clearTimeout(throttleTimer);
      if (attachRetryTimer) clearTimeout(attachRetryTimer);

      const {
        isScrollRestorePending,
        clearScrollRestorePending,
        gridScrollAnchors,
      } = useTabViewStateStore.getState();
      if (isScrollRestorePending(scrollId)) {
        clearScrollRestorePending(scrollId);
        return;
      }

      const storedAnchor = gridScrollAnchors[scrollId];
      if (storedAnchor && storedAnchor.scrollTop <= 0) {
        lastAnchorRef.current = storedAnchor;
        return;
      }

      if (
        restoreOutcomeRef.current.attempted &&
        !restoreOutcomeRef.current.succeeded
      ) {
        return;
      }

      const anchor = lastAnchorRef.current ?? captureLatestAnchor();
      if (anchor) {
        persistGridScrollAnchor({ scrollId, anchor, force: true });
      }
    };
  }, [gridApi, containerRef, scrollId]);

  useEffect(() => {
    if (!gridApi || gridApi.isDestroyed()) return;
    const activeGridApi = gridApi;

    const {
      gridScrollAnchors,
      markScrollRestorePending,
      clearScrollRestorePending,
    } = useTabViewStateStore.getState();
    const savedAnchor = gridScrollAnchors[scrollId];
    if (!savedAnchor || savedAnchor.scrollTop <= 0) {
      clearScrollRestorePending(scrollId);
      return;
    }

    const currentViewport = getAgGridViewport(containerRef);
    if (
      currentViewport &&
      shouldSkipScrollRestore({
        viewport: currentViewport,
        anchor: savedAnchor,
      })
    ) {
      clearScrollRestorePending(scrollId);
      return;
    }

    markScrollRestorePending(scrollId);
    restoreOutcomeRef.current = { attempted: true, succeeded: false };

    let cancelled = false;
    let reinforceId: number | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const restoreStartedAt = Date.now();
    const viewport = getAgGridViewport(containerRef);

    const onModelUpdated = () => {
      requestAnimationFrame(applyRestore);
    };

    const stop = ({ clearPending }: { clearPending: boolean }) => {
      if (cancelled) return;
      cancelled = true;
      if (clearPending) {
        clearScrollRestorePending(scrollId);
      }
      if (reinforceId !== null) window.clearInterval(reinforceId);
      if (timeoutId !== null) clearTimeout(timeoutId);
      if (!activeGridApi.isDestroyed()) {
        activeGridApi.removeEventListener('modelUpdated', onModelUpdated);
      }
      viewport?.removeEventListener('wheel', onUserCancel);
      viewport?.removeEventListener('pointerdown', onUserCancel);
    };

    const onUserCancel = () => {
      stop({ clearPending: true });
    };

    function applyRestore() {
      if (
        cancelled ||
        activeGridApi.isDestroyed() ||
        isLoadingGridRef.current
      ) {
        return;
      }

      const liveAnchor =
        useTabViewStateStore.getState().gridScrollAnchors[scrollId];
      if (!liveAnchor || liveAnchor.scrollTop <= 0) {
        stop({ clearPending: true });
        return;
      }

      if (
        !restoreAgGridScrollAnchor({
          gridApi: activeGridApi,
          containerRef,
          anchor: liveAnchor,
        })
      ) {
        return;
      }

      const restoredViewport = getAgGridViewport(containerRef);
      if (
        !restoredViewport ||
        !isScrollNearAnchor({
          viewport: restoredViewport,
          anchor: liveAnchor,
          tolerancePx: SCROLL_RESTORE_SUCCESS_TOLERANCE_PX,
        })
      ) {
        return;
      }

      lastAnchorRef.current = liveAnchor;
      restoreOutcomeRef.current = { attempted: true, succeeded: true };
      stop({ clearPending: true });
    }

    const scheduleAbandonTimeout = () => {
      if (timeoutId !== null) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (
          !cancelled &&
          isLoadingGridRef.current &&
          Date.now() - restoreStartedAt < SCROLL_RESTORE_MAX_WAIT_MS
        ) {
          scheduleAbandonTimeout();
          return;
        }
        stop({ clearPending: true });
      }, SCROLL_RESTORE_TIMEOUT_MS);
    };

    viewport?.addEventListener('wheel', onUserCancel, { passive: true });
    viewport?.addEventListener('pointerdown', onUserCancel);

    activeGridApi.addEventListener('modelUpdated', onModelUpdated);
    requestAnimationFrame(applyRestore);
    reinforceId = window.setInterval(applyRestore, SCROLL_RESTORE_INTERVAL_MS);
    scheduleAbandonTimeout();

    return () => {
      stop({ clearPending: false });
    };
  }, [gridApi, containerRef, scrollId]);
}
